import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useFormik } from "formik";
import * as yup from "yup";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* ICONS */
import { FiTrash } from "react-icons/fi";
import { AiFillPlusCircle } from "react-icons/ai";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* USER DEFINED COMPONENTS */
import InputField from "../../../components/input";

/* API IMPORT */
import { getSettings, updateSettings } from "../../../service/api";

const validationSchema = yup.object().shape({
  phone: yup.string().required("Phone number is required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  hours: yup
    .array()
    .of(
      yup.object().shape({
        day: yup.string().required("Day is required"),
        time: yup.string().required("Time is required"),
      })
    )
    .min(1, "Add at least one row of hours"),
});

const SiteSettings = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const formik = useFormik({
    initialValues: {
      phone: "",
      fax: "",
      email: "",
      hours: [{ day: "", time: "" }],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      updateSettings(values)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          toast.error(
            e?.response?.data?.message || "Failed to update the settings"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  /* USE-EFFECTS */
  useEffect(() => {
    getSettings()
      .then((res) => {
        if (res?.status && res?.data) {
          formik.setValues({
            phone: res.data.phone || "",
            fax: res.data.fax || "",
            email: res.data.email || "",
            hours: res.data.hours?.length
              ? res.data.hours.map((h) => ({ day: h.day, time: h.time }))
              : [{ day: "", time: "" }],
          });
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Failed to load settings");
      })
      .finally(() => {
        setFetching(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setHourAt = (index, key, value) => {
    const hours = formik.values.hours.map((h, i) =>
      i === index ? { ...h, [key]: value } : h
    );
    formik.setFieldValue("hours", hours);
  };

  const addHourRow = () => {
    formik.setFieldValue("hours", [
      ...formik.values.hours,
      { day: "", time: "" },
    ]);
  };

  const removeHourRow = (index) => {
    if (formik.values.hours.length === 1) return;
    formik.setFieldValue(
      "hours",
      formik.values.hours.filter((_, i) => i !== index)
    );
  };

  return (
    <section className="p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Site Settings | WellCare Pharmacy</title>
      </Helmet>

      <form onSubmit={formik.handleSubmit}>
        <div className="container-full-width">
          <div className="block xl:flex lg:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
                Site Settings
              </h1>
              <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
                These details appear across the website — header, footer, and
                contact page.
              </h2>
            </div>
            <Button
              type="submit"
              disabled={loading || fetching}
              className="w-full xl:w-auto lg:w-auto whitespace-nowrap mt-2 xl:mt-0 lg:mt-0 shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out"
            >
              {loading ? "Please wait..." : "Save Changes"}
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
            {/* CONTACT DETAILS */}
            <div className="col-span-1">
              <div className="bg-white rounded-lg border border-authBorder">
                <div className="px-5 py-3 border-b border-authBorder">
                  <h5 className="text-lg font-ibm-bold text-black">
                    Contact Details
                  </h5>
                  <p className="text-sm font-ibm-regular text-greyText">
                    The phone number and email address shown on the website.
                  </p>
                </div>
                <div className="p-5">
                  <div className="form-group">
                    <label className="text-sm font-ibm-regular text-greyText">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <InputField
                        type="text"
                        name="phone"
                        id="phone"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        error={
                          formik.errors.phone && formik.touched.phone
                            ? true
                            : false
                        }
                        placeholder="e.g., (385) 855-3055"
                      />
                    </div>
                    {formik.errors.phone && formik.touched.phone && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.phone}
                      </div>
                    )}
                  </div>

                  <div className="form-group mt-4">
                    <label className="text-sm font-ibm-regular text-greyText">
                      Fax Number
                    </label>
                    <p className="text-xs font-ibm-regular text-greyText">
                      Optional — leave blank to hide the fax number on the
                      website.
                    </p>
                    <div className="mt-1">
                      <InputField
                        type="text"
                        name="fax"
                        id="fax"
                        value={formik.values.fax}
                        onChange={formik.handleChange}
                        placeholder="e.g., (385) 855-3053"
                      />
                    </div>
                  </div>

                  <div className="form-group mt-4">
                    <label className="text-sm font-ibm-regular text-greyText">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <InputField
                        type="email"
                        name="email"
                        id="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={
                          formik.errors.email && formik.touched.email
                            ? true
                            : false
                        }
                        placeholder="e.g., wellcarepharmacyutah@gmail.com"
                      />
                    </div>
                    {formik.errors.email && formik.touched.email && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* HOURS OF OPERATION */}
            <div className="col-span-1">
              <div className="bg-white rounded-lg border border-authBorder">
                <div className="px-5 py-3 border-b border-authBorder">
                  <h5 className="text-lg font-ibm-bold text-black">
                    Hours of Operation
                  </h5>
                  <p className="text-sm font-ibm-regular text-greyText">
                    Each row is shown on the website exactly as written here.
                  </p>
                </div>
                <div className="p-5">
                  {formik.values.hours.map((hour, index) => (
                    <div key={index} className="flex items-start gap-2 mt-3 first:mt-0">
                      <div className="w-1/2">
                        <InputField
                          type="text"
                          name={`hours.${index}.day`}
                          id={`hours-${index}-day`}
                          value={hour.day}
                          onChange={(e) =>
                            setHourAt(index, "day", e.target.value)
                          }
                          error={
                            formik.errors.hours?.[index]?.day &&
                            formik.touched.hours
                              ? true
                              : false
                          }
                          placeholder="e.g., Monday – Friday"
                        />
                      </div>
                      <div className="w-1/2">
                        <InputField
                          type="text"
                          name={`hours.${index}.time`}
                          id={`hours-${index}-time`}
                          value={hour.time}
                          onChange={(e) =>
                            setHourAt(index, "time", e.target.value)
                          }
                          error={
                            formik.errors.hours?.[index]?.time &&
                            formik.touched.hours
                              ? true
                              : false
                          }
                          placeholder="e.g., 10:00 AM – 6:00 PM or Closed"
                        />
                      </div>
                      {formik.values.hours.length > 1 && (
                        <Button
                          onClick={() => removeHourRow(index)}
                          className="p-3 border border-greyBorder rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
                        >
                          <FiTrash className="w-4 h-4 text-greyText" />
                        </Button>
                      )}
                    </div>
                  ))}

                  <Button
                    onClick={addHourRow}
                    className="mt-4 flex items-center gap-2 text-sm shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-4 py-2.5 rounded-md border border-authBorder font-ibm-semibold text-black normal-case transition-all duration-300 ease-in-out"
                  >
                    <AiFillPlusCircle className="w-4 h-4 text-greyText" />
                    Add Row
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};

export default SiteSettings;
